import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { TBS_API_URL } from "../../config";
import type {
  BankAccount,
  BankAccountInput,
  ConfigList,
  NumberTemplate,
  NumberTemplateInput,
  Tax,
  TaxInput,
  Warehouse,
  WarehouseInput,
} from "../../types/tbs/config";

// Tiendas Biai — configuration.
//
// Reads only for the whole of Phase 1; CFG-2 (`TBS-48`) added the eight writes, because a company
// created after the seed file has no numbering, no warehouse and no account, and cannot make a
// single sale until somebody creates all three.
//
// One tag for all four. They are edited together or not at all — the settings screen shows the four
// lists at once and its checklist reads across them, so a create in any one has to refresh the rest
// anyway. Separate tags would be four cache keys for data that changes a few times a year.

export const tbsConfigApi = createApi({
  reducerPath: "tbsConfigApi",
  tagTypes: ["TbsConfig"],
  baseQuery: fetchBaseQuery({
    baseUrl: TBS_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      const tenantId = (getState() as RootState).tbsTenant.tenantId;
      if (tenantId) headers.set("X-Tenant-Id", tenantId);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getTaxes: builder.query<ConfigList<Tax>, void>({
      query: () => "/v1/taxes",
      providesTags: ["TbsConfig"],
    }),
    getWarehouses: builder.query<ConfigList<Warehouse>, void>({
      query: () => "/v1/warehouses",
      providesTags: ["TbsConfig"],
    }),
    getBankAccounts: builder.query<ConfigList<BankAccount>, void>({
      query: () => "/v1/bank-accounts",
      providesTags: ["TbsConfig"],
    }),
    /** Admin only — these carry the DIAN resolutions. A cashier's token gets 403. */
    getNumberTemplates: builder.query<ConfigList<NumberTemplate>, void>({
      query: () => "/v1/number-templates",
      providesTags: ["TbsConfig"],
    }),

    // ── Writes. Admin on all eight, including taxes and warehouses, which are `member` to read:
    // reading which drawer exists is routine, deciding which one every cash sale lands in is not.
    //
    // `If-Match` carries the version, and the server refuses a `PUT` without one. It therefore has
    // to travel from the row the form was opened with — looking it up here would fetch the current
    // version and defeat the mechanism, which is the same reason `updateContact` takes it.

    createTax: builder.mutation<Tax, TaxInput>({
      query: (body) => ({ url: "/v1/taxes", method: "POST", body }),
      invalidatesTags: ["TbsConfig"],
    }),
    updateTax: builder.mutation<Tax, { id: string; version: number; body: Partial<TaxInput> }>({
      query: ({ id, version, body }) => ({
        url: `/v1/taxes/${id}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: ["TbsConfig"],
    }),

    createWarehouse: builder.mutation<Warehouse, WarehouseInput>({
      query: (body) => ({ url: "/v1/warehouses", method: "POST", body }),
      invalidatesTags: ["TbsConfig"],
    }),
    updateWarehouse: builder.mutation<
      Warehouse,
      { id: string; version: number; body: Partial<WarehouseInput> }
    >({
      query: ({ id, version, body }) => ({
        url: `/v1/warehouses/${id}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: ["TbsConfig"],
    }),

    createBankAccount: builder.mutation<BankAccount, BankAccountInput>({
      query: (body) => ({ url: "/v1/bank-accounts", method: "POST", body }),
      invalidatesTags: ["TbsConfig"],
    }),
    updateBankAccount: builder.mutation<
      BankAccount,
      { id: string; version: number; body: Partial<BankAccountInput> }
    >({
      query: ({ id, version, body }) => ({
        url: `/v1/bank-accounts/${id}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: ["TbsConfig"],
    }),

    createNumberTemplate: builder.mutation<NumberTemplate, NumberTemplateInput>({
      query: (body) => ({ url: "/v1/number-templates", method: "POST", body }),
      invalidatesTags: ["TbsConfig"],
    }),
    updateNumberTemplate: builder.mutation<
      NumberTemplate,
      { id: string; version: number; body: Partial<NumberTemplateInput> }
    >({
      query: ({ id, version, body }) => ({
        url: `/v1/number-templates/${id}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: ["TbsConfig"],
    }),
  }),
});

export const {
  useGetTaxesQuery,
  useGetWarehousesQuery,
  useGetBankAccountsQuery,
  useGetNumberTemplatesQuery,
  useCreateTaxMutation,
  useUpdateTaxMutation,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useCreateNumberTemplateMutation,
  useUpdateNumberTemplateMutation,
} = tbsConfigApi;
